// src/services/persistence.js
//
// The lifecycle owner for client-side persistence as the app moves from
// localStorage-only (Phase 1) to Replit Postgres + localStorage cache.
//
// Three modes, switchable via environment:
//   - "localStorage"  Phase 1. localStorage is the source of truth.
//   - "dual"          Cutover. DB canonical, localStorage offline cache fallback.
//   - "db"            Steady state. localStorage is read-only legacy.
//
// One-time migration:
//   migrateLocalStorageToDB()  Admin-triggered. Reads localStorage, normalizes
//                              every record to the schema (string money ->
//                              numeric, dates -> ISO 8601, nested arrays fan
//                              out to time-series tables), POSTs to /api/migrate.
//                              Idempotent on (clientId).
//
// The current loadPersistedSites function in the monolith is the most fragile
// piece of code in the project (lessons 6, 8, 12, 13). This module replaces
// it during the Vite split and isolates each concern: read source, merge,
// dedup, normalize, write.

import { log } from "../utils/logger.js";

// ─── Configuration ────────────────────────────────────────────────────────

// Vite injects this from import.meta.env.VITE_DATA_LAYER.
// During the monolith era a `window.__DATA_LAYER__` global is read instead.
const DATA_LAYER = (typeof window !== "undefined" && window.__DATA_LAYER__) || "localStorage";

const STORAGE_KEY            = "ripple_locations_v11";
const STORAGE_KEY_DELETED    = "ripple_deleted_ids_v1";
const FX_RATES_STORAGE_KEY   = "ripple_fx_rates_v1";
const MIGRATION_FLAG_KEY     = "ripple_migration_completed_at";
const MIGRATION_BACKUP_KEY   = "ripple_migration_backup_v1";
const MIGRATION_RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Legacy keys we know how to read from. Kept short - older keys are unlikely
// to exist on the user's machine. If they do, the user should run migration.
const LEGACY_KEYS = ["ripple_locations_v10", "ripple_sites_v9", "ripple_sites_v8"];

// ─── Mode-aware reads ─────────────────────────────────────────────────────

/**
 * Top-level read for the AppShell boot sequence. Routes to the right
 * source based on DATA_LAYER and falls back gracefully.
 *
 * @param {{ api: object, sampleData: Array }} ctx
 * @returns {Promise<Array>}
 */
export async function loadLocations({ api, sampleData }) {
  if (DATA_LAYER === "db") {
    log.api("loadLocations: db mode");
    return await api.getLocations();
  }
  if (DATA_LAYER === "dual") {
    log.api("loadLocations: dual mode");
    try {
      const fresh = await api.getLocations();
      // Persist a copy to localStorage as the offline cache.
      writeLocalLocations(fresh);
      return fresh;
    } catch (err) {
      log.warn("loadLocations: db fetch failed, falling back to local cache", { err: String(err) });
      return readLocalLocations(sampleData);
    }
  }
  // Default: pure localStorage mode (Phase 1).
  return readLocalLocations(sampleData);
}

// ─── localStorage read with merge logic ───────────────────────────────────
//
// Replicates loadPersistedSites from the monolith but with each concern
// in its own function. See lessons.md for the bugs this function has
// accumulated:
//   - Lesson 6: leftover variable from a reverted replace -> NaN written
//   - Lesson 8: null fields blocked sample-data updates from propagating
//   - Lesson 12: localStorage quota blown by base64 imageUrls
//   - Lesson 13: ID rename caused dedup to fail and the portfolio to double

export function readLocalLocations(sampleData) {
  let saved = readKey(STORAGE_KEY);

  // Try legacy keys if current is empty.
  if (!saved || saved.length === 0) {
    for (const key of LEGACY_KEYS) {
      const legacy = readKey(key);
      if (legacy && legacy.length > 0) {
        log.warn("readLocalLocations: migrating from legacy key", { from: key, to: STORAGE_KEY });
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(legacy)); } catch { /* quota */ }
        saved = legacy;
        break;
      }
    }
  }

  saved = saved || [];

  // SAO-LOCATION-SPLIT-RENUMBER migration pass (migration 0216).
  // Any persisted payload that references AMER-SAO-04 by ID is referring to
  // the in-build office that was renumbered to AMER-SAO-05.  Rewrite the ID
  // in-place so stale localStorage caches reflect the new location numbering.
  // No storage-key bump is needed: this is a targeted ID fix, not a schema change.
  saved = saved.map((s) => (s.id === "AMER-SAO-04" ? { ...s, id: "AMER-SAO-05" } : s));

  // SAO-LOCATION-SPLIT-RENUMBER migration pass (migration 0216): fix AMER-SAO-03 label.
  // The old combined-location alias ("Faria Lima (Serviced)" or "Faria Lima Regus") persists
  // in stale v11 caches because mergeNullFields only fills null/undefined fields — it never
  // overwrites a non-null locationAlias.  Force the correct post-split values here.
  // Idempotent: no-op if the value is already correct.
  saved = saved.map((s) => {
    if (s.id === "AMER-SAO-03" && s.locationAlias !== "Faria Lima (Regus)") {
      return { ...s, locationAlias: "Faria Lima (Regus)", stage: "operational" };
    }
    return s;
  });

  // Fill null/undefined fields from sampleData (lesson 8).
  const sampleById = new Map(sampleData.map((s) => [s.id, s]));
  saved = saved.map((s) => mergeNullFields(s, sampleById.get(s.id)));

  // Dedup-aware append of any sampleData entries the user hasn't seen yet
  // and hasn't deleted (lesson 13).
  //
  // Address-based dedup is intentionally skipped for addresses that appear
  // more than once in sampleData itself.  Multiple sampleData entries at the
  // same address mean the address is shared by design (e.g. AMER-SAO-03
  // "Faria Lima (Regus)" and AMER-SAO-04 "Faria Lima (WeWork)" after the
  // SAO-LOCATION-SPLIT-RENUMBER migration).  Applying address dedup to these
  // pairs would suppress one location from being appended.
  const deletedIds = new Set(readKey(STORAGE_KEY_DELETED) || []);
  const savedIds   = new Set(saved.map((s) => s.id));
  const savedAddrs = new Set(saved.map((s) => s.address).filter(Boolean));

  // Identify addresses that are intentionally shared across multiple sampleData
  // entries; the address-dedup guard is bypassed for these.
  const sampleAddrCounts = {};
  for (const s of sampleData) {
    if (s.address) sampleAddrCounts[s.address] = (sampleAddrCounts[s.address] || 0) + 1;
  }
  const sharedSampleAddrs = new Set(
    Object.entries(sampleAddrCounts).filter(([, n]) => n > 1).map(([addr]) => addr)
  );

  for (const sample of sampleData) {
    if (deletedIds.has(sample.id)) continue;
    if (savedIds.has(sample.id))   continue;
    if (!sharedSampleAddrs.has(sample.address) && savedAddrs.has(sample.address)) continue;
    saved.push(sample);
  }

  return saved;
}

export function writeLocalLocations(locations) {
  // Strip imageUrl before persisting (lesson 12 - base64 blows the quota).
  const slim = locations.map(({ imageUrl, ...rest }) => rest);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
  } catch (err) {
    log.error("writeLocalLocations: persist failed", { err: String(err), count: slim.length });
  }
}

function readKey(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    log.error("readKey: parse failed", { key, err: String(err) });
    return null;
  }
}

function mergeNullFields(saved, sample) {
  if (!sample) return saved;
  const out = { ...saved };
  for (const [k, v] of Object.entries(sample)) {
    if (out[k] == null) out[k] = v;
  }
  return out;
}

// ─── One-time migration: localStorage -> DB ───────────────────────────────

/**
 * Migrate the user's localStorage data into the Replit DB. Admin-only,
 * idempotent. Records completion timestamp so the migration cannot run
 * twice. Keeps a backup of the localStorage payload for 30 days so the
 * admin can roll back without losing edits.
 *
 * @param {{ api: object, sampleData: Array, confirm: () => Promise<boolean> }} ctx
 * @returns {Promise<{ migrated: number, alreadyComplete: boolean }>}
 */
export async function migrateLocalStorageToDB({ api, sampleData, confirm }) {
  // Idempotent guard.
  const completedAt = localStorage.getItem(MIGRATION_FLAG_KEY);
  if (completedAt) {
    log.warn("migrateLocalStorageToDB: already complete", { at: completedAt });
    return { migrated: 0, alreadyComplete: true };
  }

  // Read current state.
  const local = readLocalLocations(sampleData);
  if (!local.length) {
    throw new Error("No localStorage data to migrate.");
  }

  // Confirm with admin before POST.
  const ok = await confirm({ count: local.length, sampleId: local[0]?.id });
  if (!ok) return { migrated: 0, alreadyComplete: false };

  // Backup the current localStorage payload for 30-day rollback.
  try {
    localStorage.setItem(MIGRATION_BACKUP_KEY, JSON.stringify({
      at:        new Date().toISOString(),
      locations: local,
      fxRates:   readKey(FX_RATES_STORAGE_KEY),
      deleted:   readKey(STORAGE_KEY_DELETED),
    }));
  } catch (err) {
    log.error("migrateLocalStorageToDB: backup failed", { err: String(err) });
    throw new Error("Could not write migration backup; refusing to proceed.");
  }

  // Normalize every location to the schema shape.
  const normalized = local.map(normalizeForSchema);

  // Single bulk POST. Server is responsible for the transaction.
  log.api("migrateLocalStorageToDB: posting", { count: normalized.length });
  const result = await api.bulkImport(normalized);
  if (!result || result.errors?.length) {
    throw new Error(`Migration rejected: ${(result?.errors || []).map((e) => e.message).join(", ")}`);
  }

  // Mark complete.
  localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
  log.api("migrateLocalStorageToDB: complete", { count: result.migrated });
  return { migrated: result.migrated, alreadyComplete: false };
}

/**
 * Roll back a recent migration: restore localStorage from the backup.
 * Only works within MIGRATION_RETENTION_MS of the migration. After that,
 * the backup is purged and rollback requires a server-side restore.
 */
export function rollbackMigration() {
  const completedAt = localStorage.getItem(MIGRATION_FLAG_KEY);
  if (!completedAt) throw new Error("No migration to roll back.");

  const elapsed = Date.now() - new Date(completedAt).getTime();
  if (elapsed > MIGRATION_RETENTION_MS) {
    throw new Error("Backup retention window (30 days) has elapsed. Roll back from server-side snapshot.");
  }

  const raw = localStorage.getItem(MIGRATION_BACKUP_KEY);
  if (!raw) throw new Error("Backup is missing; cannot roll back.");
  const backup = JSON.parse(raw);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(backup.locations));
    if (backup.fxRates) localStorage.setItem(FX_RATES_STORAGE_KEY, JSON.stringify(backup.fxRates));
    if (backup.deleted) localStorage.setItem(STORAGE_KEY_DELETED, JSON.stringify(backup.deleted));
    localStorage.removeItem(MIGRATION_FLAG_KEY);
    log.warn("rollbackMigration: restored", { backupAt: backup.at });
    return { restored: backup.locations.length };
  } catch (err) {
    log.error("rollbackMigration: restore failed", { err: String(err) });
    throw err;
  }
}

// ─── Schema normalization ─────────────────────────────────────────────────
//
// The contract between the localStorage shape and the DB shape. Every
// transformation lives here, in one function, so future schema changes
// have a single place to update.

export function normalizeForSchema(loc) {
  return {
    // Direct fields, snake_case for the DB.
    id:                 loc.id,
    market:             loc.market || null,
    location_alias:     loc.locationAlias || null,
    country:            loc.country || null,
    currency:           loc.currency || "USD",
    region:             loc.region,
    stage:              loc.stage,
    health:             loc.health,
    sqft:               toIntOrNull(loc.sqft),
    installed_capacity: toIntOrNull(loc.installedCapacity),
    max_capacity:       toIntOrNull(loc.maxCapacity),
    address:            loc.address || null,
    suite:              loc.suite || null,
    floors:             toIntOrNull(loc.floors),
    m_and_a:            loc.mAndA || null,
    image_url:          loc.imageUrl || null,
    district:           loc.district || null,
    lat:                toNumOrNull(loc.lat),
    lng:                toNumOrNull(loc.lng),

    // Time-series fan-out: nested arrays become rows in their own tables.
    // The server breaks these up; we just hand over the structured payload.
    leases:             (loc.leases || []).map(normalizeLease),
    projects:           (loc.projects || []).map(normalizeProject),
    candidates:         (loc.candidates || []).map(normalizeCandidate),
    commentary:         (loc.commentary || []).map(normalizeCommentary),
    utilization:        (loc.utilization || []).map((u) => normalizeUtilization(u, loc.id)),
    headcount_snapshot: normalizeHeadcountSnapshot(loc.headcount, loc.id),
    location_costs:     normalizeCostsBlock(loc.costs, loc.id, loc.currency),
  };
}

function normalizeLease(l) {
  return {
    id:                       l.id,
    status:                   l.status,
    type:                     l.type,
    vendor:                   l.vendor || null,
    lease_url:                l.leaseUrl || null,
    start_date:               normalizeDate(l.startDate),
    end_date:                 normalizeDate(l.endDate),
    total_cost:               toNumOrNull(l.totalCost),
    currency:                 l.currency || "USD",
    break_date:               normalizeDate(l.breakDate),
    earliest_termination:     normalizeDate(l.earliestTermination),
    break_notice_months:      toIntOrNull(l.breakNoticeMonths),
    renewal_notice_months:    toIntOrNull(l.renewalNoticeMonths),
    notice_period_text:       l.noticePeriodText || null,
    next_notice_deadline:     normalizeDate(l.nextNoticeDeadline),
    soonest_possible_expiration: normalizeDate(l.soonestPossibleExpiration),
    renewal_count:            toIntOrNull(l.renewalCount),
    renewal_term_years:       toIntOrNull(l.renewalTermYears),
    escalation_type:          l.escalationType || null,
    escalation_rate:          toNumOrNull(l.escalationRate),
    // LEASE-FIELDS-ADD-DROP: holdover_percent, cam_description, security_deposit_type
    // removed (dropped in migration 0216)
    free_rent_months:         toIntOrNull(l.freeRentMonths),
    tia_total_amount:         toNumOrNull(l.tiaTotalAmount),
    opex_structure:           l.opexStructure || null,
    security_deposit_amount:  toNumOrNull(l.securityDepositAmount),
    // 4 new Spacebase fields (migration 0216)
    possession_date:          l.possessionDate || null,
    lessor_contact:           l.lessorContact || null,
    rent_payment_type:        l.rentPaymentType || null,
    term_type:                l.termType || null,
    notes:                    l.notes || null,
    allowances:               (l.allowances || []).map(normalizeAllowance),
    critical_dates:           (l.criticalDates || []).map(normalizeCriticalDate),
    watch_items:              (l.watchItems || []).map(normalizeWatchItem),
  };
}

function normalizeAllowance(a) {
  return {
    id:            a.id,
    type:          a.type,
    label:         a.label || null,
    total_amount:  toNumOrNull(a.totalAmount),
    drawn_to_date: toNumOrNull(a.drawnToDate) || 0,
    currency:      a.currency || "USD",
    expiry_date:   normalizeDate(a.expiryDate),
    notes:         a.notes || null,
    draws:         (a.draws || []).map(normalizeDraw),
  };
}

function normalizeDraw(d) {
  return {
    id:          d.id,
    amount:      toNumOrNull(d.amount),
    draw_date:   normalizeDate(d.date),
    description: d.description || null,
    recorded_at: d.recordedAt || new Date().toISOString(),
  };
}

function normalizeCriticalDate(c) {
  return {
    id:          c.id || null, // server assigns if missing
    type:        c.type,
    due_date:    normalizeDate(c.dueDate || c.date),
    description: c.description || null,
  };
}

function normalizeWatchItem(w) {
  return {
    id:          w.id || null,
    type:        w.type,
    severity:    w.severity,
    status:      w.status || "open",
    title:       w.title,
    body:        w.body || null,
    resolved_at: w.resolvedAt || null,
  };
}

function normalizeProject(p) {
  return {
    id:              p.id || null,
    name:            p.name,
    type:            p.type,
    status:          p.status,
    budget:          toNumOrNull(p.budget),
    currency:        p.currency || null,
    start_date:      normalizeDate(p.startDate),
    target_end_date: normalizeDate(p.targetEndDate),
    actual_end_date: normalizeDate(p.actualEndDate),
    notes:           p.notes || null,
  };
}

function normalizeCandidate(c) {
  return {
    id:           c.id || null,
    name:         c.name,
    address:      c.address || null,
    floor:        c.floor || null,
    visited_date: normalizeDate(c.visitedDate),
    status:       c.status,
    lease_type:   c.leaseType || null,
    broker:       c.broker || null,
    pros:         Array.isArray(c.pros) ? c.pros.filter(Boolean) : [],
    cons:         Array.isArray(c.cons) ? c.cons.filter(Boolean) : [],
  };
}

function normalizeCommentary(c) {
  // id omitted on purpose. The legacy export emits synthetic string
  // ids like "c_sf_1" / "c_nyc_1" that don't satisfy the new schema's
  // `commentary.id uuid NOT NULL DEFAULT gen_random_uuid()` column —
  // attempting to insert one yields PG 22P02. The legacy ids have no
  // FK references pointing at them and the new app doesn't surface
  // them, so dropping them and letting the column DEFAULT generate a
  // uuid is the safe migration path. See the 11c cutover record.
  return {
    body:      c.text || c.body || "",
    posted_at: normalizeDate(c.date) || normalizeDate(new Date()),
  };
}

function normalizeUtilization(u, locationId) {
  // Best-effort: convert "Jan 2026" or "Jan" to a real period_start date.
  // The monolith uses both. The DB only accepts the dated form.
  const period = parseMonthLabel(u.month);
  return {
    location_id:   locationId,
    period_start:  period,
    granularity:   "month",
    avg_count:     toIntOrNull(u.avg) || 0,
    peak_count:    toIntOrNull(u.peak) || 0,
    source:        u.source || "manual",
  };
}

function normalizeHeadcountSnapshot(hc, locationId) {
  if (!hc) return null;
  return {
    location_id:   locationId,
    snapshot_date: normalizeDate(new Date()),
    ft:            toIntOrNull(hc.ft) || 0,
    cw:            toIntOrNull(hc.cw) || 0,
    ic:            toIntOrNull(hc.ic) || 0,
    source:        "manual",
  };
}

function normalizeCostsBlock(costs, locationId, currency) {
  if (!costs) return null;
  // Single-row, annual granularity. Phase 2 may break this into monthly.
  const yearStart = `${new Date().getFullYear()}-01-01`;
  return {
    location_id:   locationId,
    period_start:  yearStart,
    granularity:   "annual",
    currency:      currency || "USD",
    opex:          toNumOrNull(costs.opex),
    cam:           toNumOrNull(costs.cam),
    property_tax:  toNumOrNull(costs.propertyTax),
    parking:       toNumOrNull(costs.parking),
    janitorial:    toNumOrNull(costs.janitorial),
    security:      toNumOrNull(costs.security),
    maintenance:   toNumOrNull(costs.maintenance),
    utilities:     toNumOrNull(costs.utilities),
    waste:         toNumOrNull(costs.waste),
    it_av:         toNumOrNull(costs.itAv),
    internet:      toNumOrNull(costs.internet),
    food_beverage: toNumOrNull(costs.foodBeverage),
    fitness:       toNumOrNull(costs.fitness),
    reception:     toNumOrNull(costs.reception),
  };
}

// ─── Type coercion helpers ────────────────────────────────────────────────

function toIntOrNull(v) {
  if (v == null || v === "") return null;
  const n = parseInt(String(v).replace(/,/g, ""), 10);
  return isNaN(n) ? null : n;
}

function toNumOrNull(v) {
  if (v == null || v === "") return null;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? null : n;
}

function normalizeDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return v.toISOString().split("T")[0];
  const s = String(v).trim();
  // MM/DD/YY -> YYYY-MM-DD (assume 20XX, all portfolio dates are post-2000)
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    let [, m, d, y] = slash;
    if (y.length === 2) y = `20${y}`;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO 8601 timestamp -> date only
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (iso) return iso[1];
  return null;
}

function parseMonthLabel(label) {
  // "Jan 2026" -> "2026-01-01"; "Jan" -> assume current year
  if (!label) return null;
  const monthMap = { Jan:1, Feb:2, Mar:3, Apr:4, May:5, Jun:6, Jul:7, Aug:8, Sep:9, Oct:10, Nov:11, Dec:12 };
  const parts = label.split(/\s+/);
  const m = monthMap[parts[0]];
  if (!m) return null;
  const y = parts[1] ? parseInt(parts[1], 10) : new Date().getFullYear();
  return `${y}-${String(m).padStart(2, "0")}-01`;
}

// ─── Constants exported for tests and callers ─────────────────────────────

export const PERSISTENCE_CONFIG = {
  STORAGE_KEY,
  STORAGE_KEY_DELETED,
  FX_RATES_STORAGE_KEY,
  MIGRATION_FLAG_KEY,
  MIGRATION_BACKUP_KEY,
  MIGRATION_RETENTION_MS,
  LEGACY_KEYS,
  DATA_LAYER,
};
